import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Shield as ShieldIcon,
  Psychology as AIIcon,
} from '@mui/icons-material';
import { colors } from '../../theme';
// Removed recharts import - using custom visualization instead

interface RiskFactor {
  category: string;
  score: number;
  level: 'critical' | 'high' | 'medium' | 'low' | 'minimal';
  description: string;
  mitigations?: string[];
}

interface RiskAssessmentVisualizerProps {
  riskAssessment: {
    overall_risk_score: number;
    risk_level: 'critical' | 'high' | 'medium' | 'low' | 'minimal';
    risk_factors: Record<string, number>;
    highest_risks: string[];
    mitigation_measures: string[];
    confidence_score?: number;
  };
  aiEnhanced?: boolean;
  showDetails?: boolean;
}

const RiskAssessmentVisualizer: React.FC<RiskAssessmentVisualizerProps> = ({
  riskAssessment,
  aiEnhanced = false,
  showDetails = true,
}) => {
  const getRiskColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      case 'low': return '#388e3c';
      case 'minimal': return '#1976d2';
      default: return '#757575';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'critical':
      case 'high':
        return <ErrorIcon sx={{ color: getRiskColor(level) }} />;
      case 'medium':
        return <WarningIcon sx={{ color: getRiskColor(level) }} />;
      case 'low':
      case 'minimal':
        return <CheckIcon sx={{ color: getRiskColor(level) }} />;
      default:
        return <InfoIcon />;
    }
  };

  // Prepare data for visualization
  const riskFactorData = Object.entries(riskAssessment.risk_factors).map(([key, value]) => ({
    category: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: value as number,
    color: getRiskColor(value >= 80 ? 'critical' : value >= 60 ? 'high' : value >= 40 ? 'medium' : value >= 20 ? 'low' : 'minimal'),
  }));

  return (
    <Box>
      {/* Overall Risk Summary */}
      <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)` }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                Overall Risk Assessment
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-flex', width: 150, height: 150 }}>
                {/* Circular Progress Background */}
                <Box sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: '10px solid rgba(255,255,255,0.2)',
                }} />
                {/* Circular Progress Bar */}
                <Box sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: '10px solid',
                  borderColor: getRiskColor(riskAssessment.risk_level),
                  borderRightColor: 'transparent',
                  borderBottomColor: 'transparent',
                  transform: `rotate(${(riskAssessment.overall_risk_score / 100) * 180 - 45}deg)`,
                  transition: 'transform 1s ease',
                }} />
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {riskAssessment.overall_risk_score}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    / 100
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                Risk Level
              </Typography>
              <Chip
                label={riskAssessment.risk_level.toUpperCase()}
                sx={{
                  backgroundColor: getRiskColor(riskAssessment.risk_level),
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  py: 3,
                  px: 4,
                }}
                icon={getRiskIcon(riskAssessment.risk_level)}
              />
              {aiEnhanced && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <AIIcon sx={{ color: 'white', fontSize: 20 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    AI-Enhanced Analysis
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            {riskAssessment.confidence_score && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  Confidence Score
                </Typography>
                <Box sx={{ px: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={riskAssessment.confidence_score}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        backgroundColor: '#4caf50',
                      },
                    }}
                  />
                  <Typography variant="h5" sx={{ color: 'white', mt: 1 }}>
                    {riskAssessment.confidence_score.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Risk Factors Breakdown */}
      {showDetails && (
        <Grid container spacing={3}>
          {/* Radar Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon color="primary" />
                  Risk Factors Analysis
                </Typography>
                <Box sx={{ height: 300 }}>
                  {riskFactorData.map((factor, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {factor.category}
                        </Typography>
                        <Typography variant="body2" sx={{ color: factor.color }}>
                          {factor.value}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={factor.value}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: factor.color,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Highest Risks */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="error" />
                  Highest Risk Areas
                </Typography>
                <List dense>
                  {riskAssessment.highest_risks.map((risk, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <TrendingUpIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={risk}
                        secondary={`Priority ${index + 1}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Mitigation Measures */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShieldIcon color="success" />
                  Recommended Mitigation Measures
                </Typography>
                <Grid container spacing={2}>
                  {riskAssessment.mitigation_measures.map((measure, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Alert 
                        severity="info" 
                        icon={<CheckIcon />}
                        sx={{ 
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          '& .MuiAlert-icon': {
                            color: '#1976d2'
                          }
                        }}
                      >
                        <Typography variant="body2">
                          {measure}
                        </Typography>
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Trend Indicator */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, background: 'linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="subtitle1">
                    Risk Assessment Summary
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip
                    label={`${Object.keys(riskAssessment.risk_factors).length} Risk Factors`}
                    variant="outlined"
                    color="primary"
                  />
                  <Chip
                    label={`${riskAssessment.mitigation_measures.length} Mitigations`}
                    variant="outlined"
                    color="success"
                  />
                  {aiEnhanced && (
                    <Chip
                      label="AI-Powered"
                      icon={<AIIcon />}
                      color="secondary"
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default RiskAssessmentVisualizer;